import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import {
  useDeleteCouponMutation,
  useGetCouponQuery,
  useUpdateCouponMutation,
} from './coupons.api';
import { CouponAction } from '@topshots/core-models';
import { CouponForm } from './coupon-form';
import { PageTitle } from 'shared-components';
import Routes from '../../app/routes';

export const Coupon = () => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useGetCouponQuery(id as string);
  const [update] = useUpdateCouponMutation();
  const navigate = useNavigate();
  const [deleteCoupon] = useDeleteCouponMutation();

  if (isLoading) {
    return <span>loading...</span>;
  }

  return (
    <Formik
      initialValues={{
        code: data?.code ?? '',
        action: data?.action ?? CouponAction.FixedDiscount,
        value: data?.value ?? 0,
        isLimited: data?.isLimited ?? false,
        usageCap: data?.usageCap ?? 0,
        usageCount: data?.usageCount ?? 0,
        isActive: data?.isActive ?? false,
        expiresAt: new Date(data?.expiresAt ?? new Date())
          .toISOString()
          .split('T')[0],
      }}
      validationSchema={yup.object({
        code: yup.string().required().min(3),
        value: yup.number().required().min(0),
        usageCap: yup.number().required().min(0),
      })}
      enableReinitialize={true}
      onSubmit={async (values) => {
        await update({
          id: id as string,
          data: {
            ...values,
            expiresAt: new Date(values.expiresAt),
          },
        });
        toast.success('Coupon updated!', {
          autoClose: 2000,
        });
      }}
    >
      {({ submitForm }) => (
        <>
          <PageTitle
            title="Coupon"
            crumbs={[
              { display: Routes.DASHBOARD.title, link: Routes.DASHBOARD.link },
              { display: Routes.COUPONS.title, link: Routes.COUPONS.link },
              {
                display: data?.code ?? '',
                link: Routes.COUPON.createLink?.(id as string) ?? '',
              },
            ]}
            allowDelete={true}
            allowSave={true}
            onAction={async (action) => {
              switch (action) {
                case 'save':
                  submitForm();
                  break;
                case 'delete':
                  // eslint-disable-next-line no-restricted-globals
                  if (confirm('Are you sure you want to delete this coupon?')) {
                    await deleteCoupon(id as string);
                    navigate(Routes.COUPONS.link);
                    toast.success('Coupon deleted!');
                  }
                  break;
              }
            }}
          />
          <CouponForm />
        </>
      )}
    </Formik>
  );
};
